"use client";

// @ubm/product-ui/blocks/_conversation — états de vie PARTAGÉS des huit compositions U14 (house implementation ; original Pro
// implementation not received) : une région polie unique par composition, et la pause des boucles visuelles quand l'hôte
// d'overlay est désengagé, l'onglet caché ou la surface hors écran (l'état est conservé, seule l'animation s'arrête).
import { forwardRef, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { animate, motion, useIsPresent, useMotionValue, type HTMLMotionProps } from "motion/react";
import { motionTokens } from "@ubm/ui/motion-tokens";
import { useOverlayHostEngaged } from "@ubm/ui/overlays";
import styles from "./conversation.module.css";

/** Is this surface worth animating right now? Engaged host, visible tab, and on screen. */
export function useLiveSurface(ref: RefObject<Element | null>) {
  const engaged = useOverlayHostEngaged();
  const [tab, setTab] = useState(true);
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const read = () => setTab(document.visibilityState !== "hidden");
    read();
    document.addEventListener("visibilitychange", read);
    return () => document.removeEventListener("visibilitychange", read);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(entries => setOnScreen(entries.some(entry => entry.isIntersecting)));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return engaged && tab && onScreen;
}

/** A visually hidden polite status line. */
export function LiveStatus({ children, id }: { children: string; id?: string }) {
  return <p id={id} className={styles.srOnly} role="status" aria-live="polite">{children}</p>;
}

/** Container width of an element (ResizeObserver), for container-driven layouts that also need a JS decision. */
export function useContainerWidth(ref: RefObject<Element | null>, initial = 0) {
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(entries => { const w = entries[0]?.contentRect.width; if (w !== undefined) setWidth(w); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/** True when the primary pointer is coarse (touch): opening a field must not pop the keyboard by itself. */
export function coarsePointer() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

/**
 * A list item that leaves through an exit animation. While it leaves it is inert and hidden from assistive technology, so a
 * departing row can no longer be focused, announced, or acted on (a second shortcut would re-send a finished move).
 */
export const PresenceItem = forwardRef<HTMLLIElement, HTMLMotionProps<"li">>(function PresenceItem(props, ref) {
  return <motion.li ref={ref} {...props} {...useLeavingProps()} />;
});

/** A block that leaves through an exit animation; inert and hidden while it leaves (see PresenceItem). */
export const PresenceDiv = forwardRef<HTMLDivElement, HTMLMotionProps<"div">>(function PresenceDiv(props, ref) {
  return <motion.div ref={ref} {...props} {...useLeavingProps()} />;
});

/** Props for any element inside AnimatePresence: once it is leaving, it is inert and hidden from assistive technology. */
export function useLeavingProps() {
  const present = useIsPresent();
  return { inert: !present || undefined, "aria-hidden": !present || undefined, "data-leaving": !present || undefined } as const;
}

/** A height that follows its content on the smooth spring (ResizeObserver); instant under reduced motion or on first measure. */
export function useMeasuredHeight(reduced: boolean) {
  const inner = useRef<HTMLDivElement>(null);
  const height = useMotionValue<number | "auto">("auto");
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let first = true;
    let running: { stop: () => void } | undefined;
    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight;
      running?.stop();
      if (first || reduced) { height.jump(h); first = false; return; }
      running = animate(height, h, motionTokens.spring.smooth);
    });
    ro.observe(el);
    return () => { ro.disconnect(); running?.stop(); };
  }, [height, reduced]);
  return { inner, height };
}
